import React, { useState, useEffect, useRef, useCallback } from "react";
import { Bell, X, Check, ExternalLink, ChevronRight } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { parseUtc } from "@/lib/dateUtils";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/lib/AuthContext";
import { notificationService } from "@/api/services/notificationService";
import { campaignService } from "@/api/services/campaignService";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";

// Give up reconnecting the SSE stream after this many consecutive failures with no
// successful open in between. Prevents an expired session (stale `user` in memory but
// dead auth cookie) from reconnecting forever and storming /notifications/subscribe
// with 401s — the connection retries on the next tab refocus or page load instead.
const MAX_SSE_FAILURES = 5;

// Translate legacy backend URLs stored in older notifications to valid React Router paths.
// Current backend always sends correct React Router paths (e.g. /ManageReviews, /MyBookings).
function resolveActionUrl(url) {
  if (!url) return null;
  // Legacy old-style API path rewrites (pre-React-Router backend format)
  if (/^\/business\/[^/]+\/reviews/.test(url)) return "/ManageReviews";
  // NOTE: /business/{slug} cannot be safely converted to /BusinessDetails?id={slug}
  // because the slug is not the numeric DB id. Send users to Explore as a safe fallback.
  if (/^\/business\/[^/]+/.test(url))          return "/Explore";
  if (/^\/dashboard\/bookings/.test(url))       return "/ManageBookings";
  if (/^\/dashboard\/enquiries/.test(url))      return "/ManageEnquiries";
  if (/^\/dashboard/.test(url))                 return "/OwnerDashboard";
  if (/^\/my-bookings/.test(url))               return "/MyBookings";
  if (/^\/my-tickets/.test(url))                return "/Events";
  if (/^\/my-rewards/.test(url))                return "/MyRewards";
  if (/^\/my-claims/.test(url))                 return "/MyClaims";
  if (/^\/deals/.test(url))                     return "/Deals";
  if (/^\/events/.test(url))                    return "/Events";
  return url;
}

export default function NotificationCenter() {
  const { user, reload: reloadUser } = useAuth();
  const [notifications, setNotifications] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const esRef = useRef(null);
  const reconnectTimer = useRef(null);
  const sseFailures = useRef(0);
  const containerRef = useRef(null);

  // Close when clicking outside the dropdown
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [isOpen]);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Load initial notifications from REST endpoint.
  // Depend on user?.id (not the object reference) so a context re-render doesn't
  // re-fetch and overwrite optimistic mark-as-read updates.
  useEffect(() => {
    if (!user?.id) return;
    notificationService.getAll({ size: 30 }).then(setNotifications).catch(() => {});
  }, [user?.id]);

  // SSE subscription with exponential-backoff reconnect
  const connectSse = useCallback(() => {
    if (!user) return;
    esRef.current?.close();

    const es = notificationService.subscribe(
      (incoming) => {
        setNotifications(prev => [incoming, ...prev]);
        // Let other components react to specific notification types without opening
        // a second SSE connection. Dispatch a namespaced CustomEvent on window.
        window.dispatchEvent(new CustomEvent("fg:notification", { detail: incoming }));
        // Invalidate the full Notifications page cache so it shows the new entry
        // immediately if the page is open or when the user navigates there.
        queryClient.invalidateQueries({ queryKey: ["my-notifications"] });
        // Invalidate booking caches immediately so ManageBookings / MyBookings
        // show fresh data the moment the user navigates there — no stale flash.
        if (incoming.type?.startsWith("BOOKING_")) {
          queryClient.invalidateQueries({ queryKey: ["manage-bookings-biz"] });
          queryClient.invalidateQueries({ queryKey: ["manage-bookings-all"] });
          queryClient.invalidateQueries({ queryKey: ["owner-sidebar-badges"] });
          queryClient.invalidateQueries({ queryKey: ["my-bookings"], exact: false });
        }
        // ── Orders ──────────────────────────────────────────────────────
        // New order — owner's ManageOrders + customer's cart/order list.
        if (incoming.type === "ORDER_PLACED") {
          queryClient.invalidateQueries({ queryKey: ["business-orders"] });
          queryClient.invalidateQueries({ queryKey: ["my-orders"] });
          queryClient.invalidateQueries({ queryKey: ["cart-count"] });
        }
        // Order status changed — both sides need fresh data.
        if (incoming.type === "ORDER_UPDATE") {
          queryClient.invalidateQueries({ queryKey: ["my-orders"] });
          queryClient.invalidateQueries({ queryKey: ["business-orders"] });
        }
        if (incoming.type === "ORDER_CANCELLED") {
          queryClient.invalidateQueries({ queryKey: ["my-orders"] });
          queryClient.invalidateQueries({ queryKey: ["business-orders"] });
        }

        // ── Enquiries ─────────────────────────────────────────────────
        if (incoming.type === "ENQUIRY_RECEIVED") {
          queryClient.invalidateQueries({ queryKey: ["owner-enquiries"] });
          queryClient.invalidateQueries({ queryKey: ["owner-sidebar-badges"] });
        }
        // Owner replied to customer's enquiry.
        if (incoming.type === "REPLY") {
          queryClient.invalidateQueries({ queryKey: ["my-enquiries"] });
        }

        // ── Followers ─────────────────────────────────────────────────
        if (incoming.type === "BUSINESS_FOLLOWED") {
          queryClient.invalidateQueries({ queryKey: ["business-follow-stats"] });
        }

        // ── Deals ─────────────────────────────────────────────────────
        // New deal published — followers see it in public deals page.
        if (incoming.type === "DEAL_PUBLISHED") {
          queryClient.invalidateQueries({ queryKey: ["active-deals"] });
        }
        // Deal claimed — owner's ManageDeals needs fresh claim counts.
        if (incoming.type === "DEAL_CLAIMED") {
          queryClient.invalidateQueries({ queryKey: ["owner-deals"] });
          queryClient.invalidateQueries({ queryKey: ["active-deals"] });
        }

        // ── Events ────────────────────────────────────────────────────
        // New event published — followers see it in public events page.
        if (incoming.type === "EVENT_PUBLISHED") {
          queryClient.invalidateQueries({ queryKey: ["events"] });
        }
        // Ticket purchased — admin event stats.
        if (incoming.type === "TICKET_PURCHASED") {
          queryClient.invalidateQueries({ queryKey: ["admin-events"] });
        }

        // ── Reviews ───────────────────────────────────────────────────
        // New review — owner's review pages + public reviews + analytics.
        if (incoming.type === "REVIEW_RECEIVED") {
          queryClient.invalidateQueries({ queryKey: ["owner-all-reviews"] });
          queryClient.invalidateQueries({ queryKey: ["owner-reviews"] });
          queryClient.invalidateQueries({ queryKey: ["owner-analytics-snapshot"] });
          queryClient.invalidateQueries({ queryKey: ["reviews"] });
        }
        // Owner replied to a review — customer sees it.
        if (incoming.type === "REVIEW_REPLY") {
          queryClient.invalidateQueries({ queryKey: ["reviews"] });
          queryClient.invalidateQueries({ queryKey: ["my-reviews"] });
        }
        // Review flagged — admin + public pages.
        if (incoming.type === "REVIEW_FLAGGED") {
          queryClient.invalidateQueries({ queryKey: ["admin-reviews-all"] });
          queryClient.invalidateQueries({ queryKey: ["reviews"] });
        }

        // ── Feedback ──────────────────────────────────────────────────
        if (incoming.type === "FEEDBACK_RECEIVED") {
          queryClient.invalidateQueries({ queryKey: ["admin-feedback"] });
        }

        // ── Business lifecycle ────────────────────────────────────────
        // New business listed or system message (claim/verification submitted).
        if (incoming.type === "BUSINESS_CREATED" || incoming.type === "SYSTEM_MESSAGE") {
          queryClient.invalidateQueries({ queryKey: ["admin-all-businesses"] });
          queryClient.invalidateQueries({ queryKey: ["admin-claim-requests"] });
          queryClient.invalidateQueries({ queryKey: ["admin-verification-requests"] });
          queryClient.invalidateQueries({ queryKey: ["admin-pending-verification"] });
        }
        // Business rejected/suspended — owner and admin see updated status.
        if (incoming.type === "BUSINESS_REJECTED" || incoming.type === "BUSINESS_SUSPENDED") {
          queryClient.invalidateQueries({ queryKey: ["admin-all-businesses"] });
          queryClient.invalidateQueries({ queryKey: ["owner-businesses-ctx"] });
          queryClient.invalidateQueries({ queryKey: ["owner-sidebar-badges"] });
        }
        // Business reinstated — owner and admin lists update.
        if (incoming.type === "BUSINESS_REINSTATED") {
          queryClient.invalidateQueries({ queryKey: ["admin-all-businesses"] });
          queryClient.invalidateQueries({ queryKey: ["owner-businesses-ctx"] });
        }
        // Claim approved/business approved — user role changed, re-fetch auth.
        if (incoming.type === "CLAIM_APPROVED" || incoming.type === "BUSINESS_APPROVED") {
          reloadUser();
          queryClient.invalidateQueries({ queryKey: ["owner-businesses-ctx"] });
        }
        // Claim rejected — user sees rejection.
        if (incoming.type === "CLAIM_REJECTED") {
          queryClient.invalidateQueries({ queryKey: ["my-claims"] });
        }
        // Claim submitted — admin sees new request.
        if (incoming.type === "CLAIM_SUBMITTED") {
          queryClient.invalidateQueries({ queryKey: ["admin-claim-requests"] });
          queryClient.invalidateQueries({ queryKey: ["admin-pending-verification"] });
        }
      },
      () => {
        // EventSource fires onerror on transient network hiccups and will
        // auto-reconnect by itself (readyState === CONNECTING).  Only act when
        // the browser has given up entirely (readyState === CLOSED) — which is
        // also what a 401 / non-event-stream response produces.
        if (esRef.current?.readyState === EventSource.CLOSED) {
          esRef.current = null;
          sseFailures.current += 1;
          // Stop hammering once we've failed repeatedly with no successful open
          // (almost always an expired session). We'll try again on tab refocus.
          if (sseFailures.current > MAX_SSE_FAILURES) return;
          // Exponential backoff: 3s, 6s, 12s, 24s, 48s (capped at 60s).
          const delay = Math.min(60_000, 3_000 * 2 ** (sseFailures.current - 1));
          reconnectTimer.current = setTimeout(connectSse, delay);
        }
      }
    );
    // A successful (re)connection clears the failure streak.
    es.onopen = () => { sseFailures.current = 0; };
    esRef.current = es;
  }, [user]);

  useEffect(() => {
    connectSse();
    return () => {
      esRef.current?.close();
      clearTimeout(reconnectTimer.current);
    };
  }, [connectSse]);

  // When the user returns to a backgrounded tab, give the SSE a fresh budget and
  // reconnect if we'd previously given up (e.g. session was renewed in the meantime).
  useEffect(() => {
    const onVisible = () => {
      if (document.hidden || !user) return;
      if (!esRef.current || esRef.current.readyState === EventSource.CLOSED) {
        sseFailures.current = 0;
        clearTimeout(reconnectTimer.current);
        connectSse();
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [connectSse, user]);

  // Close the SSE connection cleanly before page unload so Firefox doesn't log
  // "connection interrupted while page was loading" — the browser would abort it
  // anyway but this prevents the console noise.
  useEffect(() => {
    const closeOnUnload = () => esRef.current?.close();
    window.addEventListener("beforeunload", closeOnUnload);
    return () => window.removeEventListener("beforeunload", closeOnUnload);
  }, []);

  const handleMarkAsRead = (id) => {
    if (!id) return;
    // Optimistic update — UI reflects the change immediately on click
    setNotifications(prev =>
      prev.map(n => String(n.id) === String(id) ? { ...n, read: true } : n)
    );
    notificationService.markRead(id).catch((err) => {
      // Revert the optimistic update if the API call fails
      console.error("Failed to mark notification as read:", err);
      setNotifications(prev =>
        prev.map(n => String(n.id) === String(id) ? { ...n, read: false } : n)
      );
    });
  };

  const handleNotificationClick = (notification) => {
    // Always mark as read on click regardless of whether there is an action URL
    handleMarkAsRead(notification.id);
    setIsOpen(false);
    // Attribute a campaign click (fire-and-forget) — does not affect navigation below.
    if (notification.campaignId) campaignService.clicked(notification.campaignId).catch(() => {});
    const url = resolveActionUrl(notification.actionUrl);
    if (url) navigate(url);
  };

  if (!user) return null;

  return (
    <div className="relative" ref={containerRef}>
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsOpen(!isOpen)}
        className="relative h-9 w-9 rounded-lg"
      >
        <Bell className="h-4 w-4" />
        {unreadCount > 0 && (
          <span className="absolute top-0 right-0 h-5 w-5 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center font-semibold">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </Button>

      {isOpen && (
        <div className="fixed left-2 right-2 top-16 w-auto sm:absolute sm:left-auto sm:right-0 sm:top-12 sm:w-80 bg-card rounded-xl border border-border shadow-lg z-50">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Notifications</h3>
            <Button variant="ghost" size="icon" onClick={() => setIsOpen(false)} className="h-6 w-6">
              <X className="h-4 w-4" />
            </Button>
          </div>

          <div className="max-h-96 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">
                <Bell className="h-8 w-8 mx-auto mb-2 opacity-50" />
                <p>No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => {
                const isUnread = !notification.read;
                const actionUrl = resolveActionUrl(notification.actionUrl);
                const timeAgo = notification.createdAt && parseUtc(notification.createdAt)
                  ? formatDistanceToNow(parseUtc(notification.createdAt), { addSuffix: true })
                  : "just now";
                return (
                  <div
                    key={notification.id}
                    className={`p-4 border-b border-border transition-colors group ${
                      actionUrl ? "cursor-pointer hover:bg-accent" : "cursor-default"
                    } ${isUnread ? "bg-primary/5" : ""}`}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="flex items-start gap-3">
                      {isUnread && (
                        <span className="mt-1.5 h-2 w-2 rounded-full bg-primary flex-shrink-0" />
                      )}
                      <div className={`flex-1 min-w-0 ${!isUnread ? "ml-5" : ""}`}>
                        <h4 className="font-semibold text-sm text-foreground truncate">{notification.title}</h4>
                        <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification.message}</p>
                        <p className="text-xs text-muted-foreground mt-1.5">{timeAgo}</p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        {isUnread && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); handleMarkAsRead(notification.id); }}
                            className="h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                            title="Mark as read"
                          >
                            <Check className="h-3.5 w-3.5" />
                          </Button>
                        )}
                        {actionUrl && (
                          <ChevronRight className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {notifications.length > 0 && (
            <div className="p-3 border-t border-border text-center">
              <button
                onClick={() => { navigate("/Notifications"); setIsOpen(false); }}
                className="text-sm text-primary hover:underline font-medium flex items-center justify-center gap-1 w-full"
              >
                View All Notifications
                <ExternalLink className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
